/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Map as MapBoxMap } from 'mapbox-gl';
import styled from 'styled-components';
import { reduxConnect } from '../../common';
import '../../../../node_modules/@weatherbug/react/dist/styles.css';
import '../../../../node_modules/mapbox-gl/dist/mapbox-gl.css';
import 'rc-slider/assets/index.css';
import { getQueryParamByName, getQueryParameterByName, updateQueryStringParameter } from '../../common/utilities/queryParameters';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { changeMapLayerIdAction } from '../../common/actions/appStateActions';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import ngDeps from '../../common/utilities/ngDependencies';
import { RefreshPopup } from './refreshPopup';
import { IStoreState, PlatformKeys, PlatformType } from '../common/interfaces/types';
import {
    Map,
    MapLayerPicker,
    AnimationWidget,
    MapLegend,
    MapControlContainer,
    MapZoomPicker,
    RasterLayer,
    Icons,
    MapInfoHeader
} from '@weatherbug/react';
import { mapAdRefreshAction } from '../../common/actions/mapAdRefreshActions';
import { MAP_USER_INTERACTION_KEYS, MAP_ZOOM_PICKER_ZOOM_LEVELS } from './constants';
import { CurrentConditionDisplay } from './currentConditionDisplay';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

const mapControlDisplayQueryParamKey = 'disablecontrols';
const mapAdsDisplayQueryParamKey = 'disableads';
const mapLegendDisplayQueryParamKey = 'disablelegend';
const { CloseIcon } = Icons;
const defaultZoom = 7;
const navHeight = 84;
const refreshMapPagePopupInterval = 600000;

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState, units, mapAdRefresh, pageVisibility } = storeState || {};
    const { mapboxAccessToken, authId, secret, mapBaseUrl, stdParameters, tileBaseUrl, airQualityAttributionUrl, platform, user, defaultMapLayerId, activeRoute } = appState?.appState || {};
    const { tempUnit, pressureUnit, rainUnit, windUnit } = units;
    const { ActiveLocation, IsBot } = user || {};
    const { Locality } = ActiveLocation || {};
    const { Latitude, Longitude, SlugName, DisplayCompositeName } = Locality || {};
    const { userMapInteraction } = mapAdRefresh || {};
    const { visible } = pageVisibility || {};

    return {
        latitude: Latitude,
        longitude: Longitude,
        locationSlugName: SlugName,
        mapboxAccessToken,
        authId,
        secret,
        mapBaseUrl,
        tileBaseUrl,
        tempUnit,
        pressureUnit,
        rainUnit,
        windUnit,
        stdParameters, 
        airQualityAttributionUrl,
        isBot: IsBot,
        platform,
        userMapInteraction,
        pageTabIsActive: visible,
        defaultMapLayerId,
        currentLocationName: DisplayCompositeName,
        isMapsPage: activeRoute === 'maps'
    };
};

const mapDispatchToProps = { changeMapLayerIdAction, mapAdRefreshAction };

interface MapsScreenType {
    latitude: number;
    longitude: number;
    locationSlugName: string;
    authId: string;
    secret: string;
    mapBaseUrl: string;
    tileBaseUrl: string;
    mapboxAccessToken: string;
    tempUnit: number;
    pressureUnit: number;
    rainUnit: number;
    windUnit: number;
    stdParameters: Record<string, unknown>;
    config: Record<string, unknown>;
    changeMapLayerIdAction: (layerId: string) => void;
    mapAdRefreshAction: (userAction: string) => void;
    userMapInteraction: string;
    pageTabIsActive: boolean;
    isMobile: boolean;
    airQualityAttributionUrl: string;
    isBot: boolean;
    platform: PlatformType;
    defaultMapLayerId: string;
    currentLocationName: string;
    isMapsPage: boolean;
}

const MapsScreen = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(withScreenSize(({ 
    latitude = 0,
    longitude = 0,
    locationSlugName,
    authId,
    secret,
    mapBaseUrl,
    tileBaseUrl,
    mapboxAccessToken,
    tempUnit,
    pressureUnit,
    rainUnit,
    windUnit,
    stdParameters,
    userMapInteraction,
    changeMapLayerIdAction,
    mapAdRefreshAction,
    pageTabIsActive,
    isMobile,
    airQualityAttributionUrl,
    defaultMapLayerId,
    isBot, 
    platform,
    currentLocationName,
    isMapsPage
}: MapsScreenType) => {
    const [ currentLatitude, setCurrentLatitude ] = useState(latitude);
    const [ currentLongitude, setCurrentLongitude ] = useState(longitude);
    const [ currentLocationSlugName, setCurrentLocationSlugName ] = useState(locationSlugName);
    const [ centerMarkerLatitude, setCenterMarkerLatitude ] = useState(latitude);
    const [ centerMarkerLongitude, setCenterMarkerLongitude ] = useState(longitude);
    const [ currentLayerId, setCurrentLayerId ] = useState<string>(defaultMapLayerId);
    const [ enableLayerPicker, setEnableLayerPicker ] = useState(true);
    const [ enableZoomPicker, setEnableZoomPicker ] = useState(false);
    const [ layerOpacity, setLayerOpacity ] = useState(70);
    const [ showOverlays, setShowOverlays] = useState(true);
    const [showMapInfoHeader, setShowMapInfoHeader] = useState(true);
    const [ height, setHeight] = useState<number>();
    const [ showRefreshPopup, setShowRefreshPopup] = useState(false);
    const pageVisibilityRef = useRef(pageTabIsActive);
    const currentZoomRef = useRef(defaultZoom);
    const isInitialLoadComplete = useRef(false);
    const [ currentZoom, setCurrentZoom ] = useState<number>();

    useEffect(() => {        
        if (!pageVisibilityRef.current && pageTabIsActive) {
            mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.mapsTabInactiveToActive);  
        }
        pageVisibilityRef.current = pageTabIsActive;
    }, [pageTabIsActive, mapAdRefreshAction]);

    useEffect(() => {
        if (!isInitialLoadComplete.current && defaultMapLayerId) {
            isInitialLoadComplete.current = true;
            const layerIdFromUrl = getQueryParameterByName('layerId');
            const center = getQueryParameterByName('center');

            if (isMobile) {
                setEnableLayerPicker(false);
            }

            const queryValue = getQueryParameterByName('s');    
            if (queryValue) {
                setHeight(window.innerHeight);
            } else {
                window.innerHeight - navHeight;
            }

            //Hide overlays if the s parameter is passed and matches the config value.  This is for the mac desktop app
            if(queryValue && queryValue !== '') {
                setShowOverlays(platform?.[queryValue as PlatformKeys]?.ShowMapOverlays);
                setShowMapInfoHeader(platform?.[queryValue as PlatformKeys]?.ShowMapOverlays);
            }

            if (layerIdFromUrl) {
                setCurrentLayerId(layerIdFromUrl);
            } else {
                setCurrentLayerId(defaultMapLayerId);
                const newUrl = updateQueryStringParameter(window.location.href, 'layerId', currentLayerId);
                window.history.pushState('', '', newUrl);
            }
 
            if (center) {
                const centerArr = center.split(',');
                setCurrentLatitude(Number(centerArr[0]));
                setCurrentLongitude(Number(centerArr[1]));
                setCenterMarkerLatitude(Number(centerArr[0]));
                setCenterMarkerLongitude(Number(centerArr[1]));
                if (centerArr[2]) {
                    setCurrentZoom(Number(centerArr[2]));
                }
            } else {
                setCurrentZoom(defaultZoom);
                setCenterUrlParam(currentLatitude, currentLongitude, defaultZoom);
            }

            changeMapLayerIdAction(currentLayerId);
            window.scrollTo(0, 0);
            window.addEventListener('resize', updateDimensions);

            setRefreshPopupFunctionality();   
        }
    }, [defaultMapLayerId, isMobile]);

    useEffect(() => {
        const didUpdateLocationBySlugName = locationSlugName && locationSlugName && locationSlugName !== currentLocationSlugName;

        if (didUpdateLocationBySlugName
         || currentLatitude === 0 && currentLongitude === 0 && latitude && longitude && locationSlugName !== currentLocationSlugName) {
            setCenterUrlParam(latitude, longitude);
            const queryValue = getQueryParameterByName('s');
            setCurrentLatitude(latitude);
            setCurrentLongitude(longitude);
            setCenterMarkerLatitude( latitude);
            setCenterMarkerLongitude(longitude);
            setHeight(queryValue ? window.innerHeight : window.innerHeight - navHeight);
            setCurrentLocationSlugName(locationSlugName);
        }
    }, [latitude, longitude, locationSlugName]);

    const onPanned = (latitude: number, longitude: number) => {
        setCenterUrlParam(latitude, longitude, undefined);
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.mouseGrabPan}_${latitude}_${longitude}`);
    };

    const onZoomChanged = (zoom: number) => {
        setCenterUrlParam(undefined, undefined, zoom);
    };

    const setCenterUrlParam = (newLatitude?: number, newLongitude?: number, newZoom?: number) => {
        const center = getQueryParameterByName('center');
        let centerUrlParamLatitude;
        let centerUrlParamLongitude;
        let centerUrlParamLatitudeZoom;

        if (center) {
            const centerArr = center.split(',');
            centerUrlParamLatitude = newLatitude || centerArr[0];
            centerUrlParamLongitude = newLongitude || centerArr[1];
            centerUrlParamLatitudeZoom = newZoom || centerArr[2];
        } else {
            centerUrlParamLatitude = newLatitude || latitude;
            centerUrlParamLongitude = newLongitude || longitude;
            centerUrlParamLatitudeZoom = newZoom || defaultZoom;
        }

        if(centerUrlParamLatitude && centerUrlParamLongitude && centerUrlParamLatitudeZoom) {
            const newCenter = `${centerUrlParamLatitude},${centerUrlParamLongitude},${centerUrlParamLatitudeZoom}`;
            const newUrl = updateQueryStringParameter(window.location.href, 'center', newCenter);
            ngDeps?.rootScope?.$apply(() => {
                window.history.replaceState('', '', newUrl);
            });
        }
    };

    const onLayerChanged = (layerId: string) => {
        const mapCurrentLayerId = currentLayerId;

        if (layerId !== mapCurrentLayerId) {
            setCurrentLayerId(layerId);
            changeMapLayerIdAction(layerId);
            mapAdRefreshAction(layerId);
            const newUrl = updateQueryStringParameter(window.location.href, 'layerId', layerId);
            ngDeps?.rootScope?.$apply(() => {
                window.history.pushState('', '', newUrl);
            });
        }
    };

    const updateDimensions = () => {
        setHeight(window.innerHeight);
    };

    let setRefreshMapPagePopupInterval: number;
    const setRefreshPopupFunctionality = () => {      
        setRefreshMapPagePopupInterval = window.setInterval(() => {
            setShowRefreshPopup(true);
            clearInterval(setRefreshMapPagePopupInterval);
        }, refreshMapPagePopupInterval);
    };

    useEffect(() => {
        return () => {
            window.removeEventListener('resize', updateDimensions);
            clearInterval(setRefreshMapPagePopupInterval);
        };
    }, []);

    const toggleLayerPicker = (enableLayerPicker: boolean) => {
        const newEnableZoomPicker = enableLayerPicker ? false : enableZoomPicker;
        setEnableLayerPicker(enableLayerPicker);
        setEnableZoomPicker(newEnableZoomPicker);

        if (enableLayerPicker) {
            mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.layersButtonOpenWeatherLayersMenu);
        } else {
            mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.layersButtonCloseWeatherLayersMenu);
        }
    };

    const toggleZoomPicker = (enableZoomPicker: boolean) => {
        const newEnableLayerPicker = enableZoomPicker ? false : enableLayerPicker;
        setEnableZoomPicker(enableZoomPicker);
        setEnableLayerPicker(newEnableLayerPicker);
    };

    const adRefreshActionOnPresetZoom = useCallback((zoomLevel: number) => {
        if (
            zoomLevel !== currentZoom
            && MAP_ZOOM_PICKER_ZOOM_LEVELS.includes(
                currentZoom as typeof MAP_ZOOM_PICKER_ZOOM_LEVELS[number]
            )
        ) {
            mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.presetZoom}_${zoomLevel}`);
        }

        setCurrentZoom(zoomLevel);
    }, []);

    const adRefreshActionOnScrollWheelZoom = useCallback((zoomLevel: number) => {
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.scrollWheelZoom}_${zoomLevel}`);
    }, []);

    const adRefreshActionOnDoubleClickZoomIn = useCallback((zoomLevel: number) => {
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.doubleClickZoom}_${zoomLevel}`);
    }, []);

    const adRefreshActionOnPlusButtonClickZoomIn = useCallback((zoomLevel: number) => {
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.plusButtonClickZoomIn}_${zoomLevel}`);
    }, []);

    const adRefreshActionOnMinusButtonClickZoomOut = useCallback((zoomLevel: number) => {
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.minusButtonClickZoomOut}_${zoomLevel}`);
    }, []);

    const adRefreshActionOnAnimationStart = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.playAnimation);
    }, []);

    const adRefreshActionOnAnimationStop = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.pauseAnimation);
    }, []);

    const adRefreshActionOnMapLegendToggleOff = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.infoButtonHideLegend);
    }, []);

    const adRefreshActionOnMapLegendToggleOn = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.infoButtonShowLegend);
    }, []);

    const adRefreshActionOnMapOverlayCloseIconClick = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.closeWeatherLayersMenuCloseIcon);
    }, []);

    const adRefreshActionCycleSevenSlotsAnimation = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.cycleSevenSlotsAnimation);
    }, []);

    const adRefreshActionOnOpacitySliderChangeComplete = useCallback((layerOpacity: number) => {
        mapAdRefreshAction(`${MAP_USER_INTERACTION_KEYS.onOpacitySliderChangeComplete}_${layerOpacity}`);
    }, []);

    const adRefreshActionClickPanToMarkerButton = useCallback(() => {
        mapAdRefreshAction(MAP_USER_INTERACTION_KEYS.clickPanToMarkerButton);
    }, []);

    const onClickHeaderImage = useCallback(() => {
        toggleLayerPicker(false);
        adRefreshActionOnMapOverlayCloseIconClick();
    }, [adRefreshActionOnMapOverlayCloseIconClick]);

    if (!mapboxAccessToken || !authId || !secret || !mapBaseUrl) {
        return null;
    }

    const queryParamValueIsTrue = (paramValue: string | null) => {
        if (!paramValue) {
            return;
        }

        return paramValue.toLowerCase() === 'true';
    };
    const shouldDisableMapControls = queryParamValueIsTrue(getQueryParamByName(mapControlDisplayQueryParamKey));
    const shouldDisableMapAds = queryParamValueIsTrue(getQueryParamByName(mapAdsDisplayQueryParamKey));
    const shouldDisableMapLegend = queryParamValueIsTrue(getQueryParamByName(mapLegendDisplayQueryParamKey));

    return (
        <Container>
            { showRefreshPopup 
                && <RefreshPopup 
                    open={showRefreshPopup} 
                    closeRefreshPopup={() => setShowRefreshPopup(false)} 
                    setRefreshPopupFunctionality={() => {setRefreshPopupFunctionality();}}
                /> 
            }
            <Map
                mapboxAccessToken={mapboxAccessToken}
                initialLongitude={currentLongitude}
                initialLatitude={currentLatitude}
                initialZoom={currentZoom}
                longitude={longitude}
                latitude={latitude}
                zoom={currentZoom}
                enableDragRotate={true}
                enablePan={true}
                enableZoom={true}
                height={height}
                isBot={isBot}
                showFullscreenControl={false}
                showNavigationControl={false}
                showCenterMarker={true}
                centerMarkerLatitude={centerMarkerLatitude}
                centerMarkerLongitude={centerMarkerLongitude}
                smallPinImageUrl='img/map-pin-active.png'
                bigPinImageUrl='img/map-pin-active.png'
                onPanned={onPanned}
                onZoomChanged={onZoomChanged}
                adRefreshActionOnPlusButtonClickZoomIn={adRefreshActionOnPlusButtonClickZoomIn}
                adRefreshActionOnMinusButtonClickZoomOut={adRefreshActionOnMinusButtonClickZoomOut}
                adRefreshActionClickPanToMarkerButton={adRefreshActionClickPanToMarkerButton}
                extension={(
                    map: InstanceType<typeof MapBoxMap>, 
                    latitude: number, 
                    longitude: number, 
                    mapZoomOutPressed: () => void, 
                    mapZoomInPressed: () => void, 
                    mapPanToMarkerPressed: () => void, 
                    currentZoomLevel: number, 
                    changeZoom: (value: number) => void
                ) => {
                    // eslint-disable-next-line no-extra-parens
                    return (map || (isBot && !map)) && <RasterLayer
                        map={map}
                        airQualityAttributionUrl={airQualityAttributionUrl}
                        latitude={latitude}
                        longitude={longitude}
                        defaultLayerId={currentLayerId}
                        mapBaseUrl={mapBaseUrl}
                        tileBaseUrl={tileBaseUrl}
                        authId={authId}
                        secret={secret}
                        stdApiParameters={stdParameters}
                        layerOpacity={layerOpacity}
                        tempUnit={tempUnit}
                        pressureUnit={pressureUnit}
                        rainUnit={rainUnit}
                        windUnit={windUnit}
                        adRefreshActionOnAnimationStart={adRefreshActionOnAnimationStart}
                        adRefreshActionOnAnimationStop={adRefreshActionOnAnimationStop}
                        adRefreshActionCycleSevenSlotsAnimation={adRefreshActionCycleSevenSlotsAnimation}
                        onLayerChanged={onLayerChanged}
                        adRefreshActionOnScrollWheelZoom={adRefreshActionOnScrollWheelZoom}
                        adRefreshActionOnDoubleClickZoomIn={adRefreshActionOnDoubleClickZoomIn}
                        controlContainer={(
                            layers: [],
                            onLayerPressed: () => void,
                            currentLayerId: string,
                            animationEnabled: boolean,
                            animationTime: string,
                            animationCallback: () => void,
                            progressPercentage: number,
                            legendColors: [],
                            legendLabels: [],
                            alertsLegend: [],
                            legendSets: [],
                            legendUnits: string,
                            isAnimating: boolean
                        ) => {
                            return (
                                showOverlays && !shouldDisableMapControls && <div>
                                    <CurrentConditionDisplay
                                        isMapLayerPickerClosed={!enableLayerPicker}
                                    />
                                    <MapControlContainer
                                        layers={layers}
                                        onLayerPressed={onLayerPressed}
                                        currentLayerId={currentLayerId}
                                        animationEnabled={animationEnabled}
                                        isAnimating={isAnimating}
                                        animationTime={animationTime}
                                        animationCallback={animationCallback}
                                        progressPercentage={progressPercentage}
                                        legendColors={!shouldDisableMapLegend ? legendColors : []}
                                        legendLabels={!shouldDisableMapLegend ? legendLabels : []}
                                        alertsLegend={!shouldDisableMapLegend ? alertsLegend : []}
                                        legendSets={!shouldDisableMapLegend ? legendSets : []}
                                        mapZoomOutPressed={mapZoomOutPressed}
                                        mapZoomInPressed={mapZoomInPressed}
                                        mapPanToMarkerPressed={mapPanToMarkerPressed}
                                        currentZoomLevel={currentZoom}
                                        changeZoom={changeZoom}
                                        enableLayerPicker={enableLayerPicker}
                                        enableZoomPicker={enableZoomPicker}
                                        onToggleLayerPicker={toggleLayerPicker}
                                        onToggleZoomPicker={toggleZoomPicker}
                                        zoomInButtonEnabled={!isMobile}
                                        zoomOutButtonEnabled={!isMobile}
                                        legendUnits={legendUnits}
                                        adRefreshActionOnMapLegendToggleOff={adRefreshActionOnMapLegendToggleOff}
                                        adRefreshActionOnMapLegendToggleOn={adRefreshActionOnMapLegendToggleOn}
                                        adRefreshActionOnPresetZoom={adRefreshActionOnPresetZoom}
                                        layerPicker={(
                                            layers: [],
                                            onLayerPressed: () => void,
                                            currentLayerId: string
                                        ) => {
                                            return (
                                                <MapLayerPicker
                                                    mapBaseUrl={mapBaseUrl}
                                                    authId={authId}
                                                    secret={secret}
                                                    longitude={longitude}
                                                    latitude={latitude}
                                                    stdApiParameters={stdParameters}
                                                    headerImage={<CloseIcon />}
                                                    onClickHeaderImage={onClickHeaderImage}
                                                    adRefreshActionOnOpacitySliderChangeComplete={adRefreshActionOnOpacitySliderChangeComplete}
                                                    layers={layers}
                                                    onLayerPressed={onLayerPressed}
                                                    currentLayerId={currentLayerId}
                                                    shouldShowSlider={true}
                                                    sliderMinLabel={'Lighter'}
                                                    sliderMaxLabel={'Darker'}
                                                    layerOpacity={layerOpacity}
                                                    layerOpacityChanged={(value: number) => {
                                                        setLayerOpacity(value);
                                                    }}
                                                />
                                            );
                                        }}
                                        legend={(
                                            legendColors: [],
                                            legendLabels: [],
                                            alertsLegend: [],
                                            legendSets: [],
                                            legendUnits: string
                                        ) => {
                                            return (
                                                <MapLegend
                                                    legendColors={legendColors}
                                                    legendLabels={legendLabels}
                                                    alertsLegend={alertsLegend}
                                                    legendSets={legendSets}
                                                    legendUnits={legendUnits}
                                                    applyPresetTopPadding={isMapsPage}
                                                />
                                            );
                                        }}
                                        animationControl={(
                                            animationEnabled: boolean,
                                            animationTime: string,
                                            animationCallback: () => void,
                                            progressPercentage: number,
                                            isAnimating: boolean
                                        ) => {
                                            return (
                                                <AnimationWidget
                                                    animationEnabled={animationEnabled}
                                                    animationTime={animationTime}
                                                    animationCallback={animationCallback}
                                                    progressPercentage={progressPercentage}
                                                    isAnimating={isAnimating}
                                                />
                                            );
                                        }}
                                        zoomPicker={(
                                            currentZoomLevel: number,
                                            onZoomChanged: () => void
                                        ) => {
                                            return (
                                                <MapZoomPicker
                                                    headerImage={<CloseIcon />}
                                                    onClickHeaderImage={onClickHeaderImage}
                                                    currentZoomLevel={currentZoom}
                                                    onItemPressed={onZoomChanged}
                                                />
                                            );
                                        }}
                                    />
                                    {
                                        !shouldDisableMapAds && <>
                                            <MobileOverlayAd>
                                                {isMobile && <DisplayAdUnit
                                                    width={320}
                                                    height={50}
                                                    refreshWatchParam={userMapInteraction}
                                                    slotId='maps_x83'/>}
                                            </MobileOverlayAd>
                                            <OverlayAd>
                                                {!isMobile
                                                    && <DisplayAdUnit
                                                        refreshWatchParam={userMapInteraction}
                                                        width={300}
                                                        height={250}
                                                        slotId='maps_x13' />}
                                            </OverlayAd>
                                        </>
                                    }
                                </div>
                            );
                        }}
                        currentLocation={currentLocationName}
                        infoHeader={(
                            layerName: string,
                            selectedSlotDate: string,
                            selectedSlotTime: string,
                        ) => {
                            return (
                                <>
                                    { showMapInfoHeader && <MapInfoHeader layerName={layerName} selectedSlotDate={selectedSlotDate} selectedSlotTime={selectedSlotTime} currentLocation={currentLocationName} /> }
                                </>
                            );
                        }}
                    />
                    ;
                }}
            />
        </Container>
    );
})));

export { MapsScreen as default };

const Container = styled.div`
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    display: flex;
`;

const MobileOverlayAd = styled.div`
    position: absolute;
    left: 0px;
    right: 0px;
    bottom: 45px;
`;

const OverlayAd = styled.div`
    position: absolute;
    right: 4px;
    bottom: 34px;
`;
